import type { ScheduleView } from '../lib/reports';
import { formatDate, formatRs } from '../lib/money';
import { DocHeader } from '../components/PrintPreview';
import StaffStamp from '../components/StaffStamp';

/**
 * The EPF/ETF contribution schedule — the return submitted to the fund — and
 * the overall statement.
 *
 * "Contribution figures are grouped by pay run and rounded exactly as the run
 * rounds them, so a statement always ties back to the payslips to the rupee."
 * Every figure here is read straight from the `contributions` row that was
 * written when the run was approved. Nothing is recalculated on the way to the
 * paper.
 */

interface ScheduleProps {
  schedule: ScheduleView;
  printedBy: string;
  printedAt: Date;
}

export function ContributionSchedule({ schedule, printedBy, printedAt }: ScheduleProps) {
  const { payment, lines } = schedule;

  const totals = lines.reduce(
    (acc, line) => ({
      base: acc.base + line.base_rs,
      employee: acc.employee + line.epf_employee_rs,
      employer: acc.employer + line.epf_employer_rs,
      etf: acc.etf + line.etf_rs,
    }),
    { base: 0, employee: 0, employer: 0, etf: 0 },
  );

  return (
    <>
      <DocHeader
        doc="EPF and ETF contribution schedule"
        title={`Month ${payment.period_month}`}
        sub={
          `Paid ${formatDate(payment.paid_on)} · reference ${payment.reference ?? '—'} · ` +
          `recorded by ${payment.created_by_name} · printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`
        }
      />

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Member</th>
            <th>EPF number</th>
            <th>Pay run</th>
            <th className="num">Base salary</th>
            <th className="num">Employee</th>
            <th className="num">Employer</th>
            <th className="num">EPF total</th>
            <th className="num">ETF</th>
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr><td colSpan={10}>No member detail recorded for this payment.</td></tr>
          )}
          {lines.map((line, index) => (
            <tr key={`${line.worker_id}-${line.period_from}`}>
              <td>{index + 1}</td>
              <td>{line.worker_name}</td>
              <td>{line.epf_no ?? '—'}</td>
              <td>
                {formatDate(line.period_from)} – {formatDate(line.period_to)}
              </td>
              <td className="num">{formatRs(line.base_rs)}</td>
              <td className="num">{formatRs(line.epf_employee_rs)}</td>
              <td className="num">{formatRs(line.epf_employer_rs)}</td>
              <td className="num">{formatRs(line.epf_total_rs)}</td>
              <td className="num">{formatRs(line.etf_rs)}</td>
              <td className="num">{formatRs(line.epf_total_rs + line.etf_rs)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={4}>{payment.member_count} members</th>
            <th className="num">{formatRs(totals.base)}</th>
            <th className="num">{formatRs(totals.employee)}</th>
            <th className="num">{formatRs(totals.employer)}</th>
            <th className="num">{formatRs(totals.employee + totals.employer)}</th>
            <th className="num">{formatRs(totals.etf)}</th>
            <th className="num">{formatRs(totals.employee + totals.employer + totals.etf)}</th>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: 10, fontSize: 12 }}>
        EPF paid <b>{formatRs(payment.epf_rs)}</b> · ETF paid <b>{formatRs(payment.etf_rs)}</b> ·
        total <b>{formatRs(payment.epf_rs + payment.etf_rs)}</b>. Contributions are on base
        salary only.
      </div>

      <div className="siglines">
        {/* Only the owner records a remittance, so he both paid and approved it. */}
        <StaffStamp label="Paid by" userId={payment.created_by} name={payment.created_by_name} at={payment.created_at} />
        <StaffStamp label="Approved" userId={payment.created_by} name={payment.created_by_name} at={payment.created_at} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------- statement

interface StatementProps {
  statement: {
    runs: {
      id: string; period_from: string; period_to: string; base_rs: number;
      epf_employee_rs: number; epf_employer_rs: number; etf_rs: number;
    }[];
    payments: {
      period_month: string; paid_on: string; reference: string | null;
      epf_rs: number; etf_rs: number; member_count: number;
    }[];
  };
  printedBy: string;
  printedAt: Date;
}

export function FundStatement({ statement, printedBy, printedAt }: StatementProps) {
  const accrued = statement.runs.reduce(
    (acc, run) => ({
      base: acc.base + run.base_rs,
      employee: acc.employee + run.epf_employee_rs,
      employer: acc.employer + run.epf_employer_rs,
      etf: acc.etf + run.etf_rs,
    }),
    { base: 0, employee: 0, employer: 0, etf: 0 },
  );
  const paid = statement.payments.reduce(
    (acc, p) => ({ epf: acc.epf + p.epf_rs, etf: acc.etf + p.etf_rs }),
    { epf: 0, etf: 0 },
  );
  const epfDue = accrued.employee + accrued.employer;

  return (
    <>
      <DocHeader
        doc="EPF and ETF statement"
        title="All approved pay runs to date"
        sub={`Printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`}
      />

      <table>
        <thead>
          <tr>
            <th>Pay run</th>
            <th className="num">Base salary</th>
            <th className="num">EPF employee</th>
            <th className="num">EPF employer</th>
            <th className="num">ETF</th>
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {statement.runs.length === 0 && <tr><td colSpan={6}>Nothing accrued yet.</td></tr>}
          {statement.runs.map((run) => (
            <tr key={run.id}>
              <td>
                {formatDate(run.period_from)} – {formatDate(run.period_to)}
              </td>
              <td className="num">{formatRs(run.base_rs)}</td>
              <td className="num">{formatRs(run.epf_employee_rs)}</td>
              <td className="num">{formatRs(run.epf_employer_rs)}</td>
              <td className="num">{formatRs(run.etf_rs)}</td>
              <td className="num">
                {formatRs(run.epf_employee_rs + run.epf_employer_rs + run.etf_rs)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Totals</th>
            <th className="num">{formatRs(accrued.base)}</th>
            <th className="num">{formatRs(accrued.employee)}</th>
            <th className="num">{formatRs(accrued.employer)}</th>
            <th className="num">{formatRs(accrued.etf)}</th>
            <th className="num">{formatRs(epfDue + accrued.etf)}</th>
          </tr>
        </tfoot>
      </table>

      <h4 style={{ marginTop: 14 }}>Payments made to the funds</h4>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Paid on</th>
            <th>Reference</th>
            <th className="num">Members</th>
            <th className="num">EPF</th>
            <th className="num">ETF</th>
          </tr>
        </thead>
        <tbody>
          {statement.payments.length === 0 && <tr><td colSpan={6}>Nothing remitted yet.</td></tr>}
          {statement.payments.map((p, index) => (
            <tr key={`${p.period_month}-${index}`}>
              <td>{p.period_month}</td>
              <td>{formatDate(p.paid_on)}</td>
              <td>{p.reference ?? '—'}</td>
              <td className="num">{p.member_count}</td>
              <td className="num">{formatRs(p.epf_rs)}</td>
              <td className="num">{formatRs(p.etf_rs)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={4}>Remitted</th>
            <th className="num">{formatRs(paid.epf)}</th>
            <th className="num">{formatRs(paid.etf)}</th>
          </tr>
          <tr>
            <th colSpan={4}>Still to remit</th>
            <th className="num">{formatRs(epfDue - paid.epf)}</th>
            <th className="num">{formatRs(accrued.etf - paid.etf)}</th>
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: 10, fontSize: 12 }}>
        All figures are on base salary only — never the add-on, overtime, allowances or
        Sunday money.
      </div>
    </>
  );
}
